import React, { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import { useProduct } from 'api/useProduct';
import {
  RollingOfferProductSequence,
  RollingSequenceProduct
} from 'common/contracts';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from 'components/ui/table';

const MAX_PRODUCTS = 10;

interface ProductOption {
  value: string;
  label: string;
  image?: string;
  publisherProductId?: string;
}

interface CardProductsTableProps {
  productSequenceItem: RollingOfferProductSequence;
  sequenceIndex: number;
  onProductChange: (productIndex: number, productId: string | null) => void;
  onQuantityChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    productIndex: number
  ) => void;
  onRemoveProduct: (productIndex: number) => void;
  onAddProduct: () => void;
  localQuantities: Record<number, string>;
}

const CardProductsTable: React.FC<CardProductsTableProps> = ({
  productSequenceItem,
  sequenceIndex,
  onProductChange,
  onQuantityChange,
  onRemoveProduct,
  onAddProduct,
  localQuantities
}) => {
  const { publisherId } = useParams();
  const { data: productsData } = useProduct(publisherId).getProducts;

  const productOptions: ProductOption[] = useMemo(() => {
    if (!productsData?.result) return [];
    return productsData.result.map((product: Product) => ({
      value: product.productId,
      label: product.name,
      image: product.images?.[0]?.url,
      publisherProductId: product.publisherProductId
    }));
  }, [productsData?.result]);

  const products = productSequenceItem?.products || [];
  const canAddMore = products.length < MAX_PRODUCTS;

  const getSelectedOption = (prod: RollingSequenceProduct) => {
    const productId = prod?.product?.productId;
    if (!productId) return undefined;
    const option = productOptions.find((opt) => opt.value === productId);
    return option ? { value: option.value, label: option.label } : undefined;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-base-border rounded-lg overflow-hidden">
        <Table disableScrollWrapper>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent border-b border-base-border">
              <TableHead className="text-sm font-medium text-base-foreground h-12 px-4 border-r border-base-border w-1/2">
                Product
              </TableHead>
              <TableHead className="text-sm font-medium text-base-foreground h-12 px-4 w-1/2">
                Amount
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map(
              (prod: RollingSequenceProduct, productIndex: number) => (
                <TableRow
                  key={productIndex}
                  className="hover:bg-transparent border-b border-base-border last:border-b-0"
                  data-testid={`buyOneGetManyProductRow-${productIndex + 1}`}
                >
                  <TableCell className="px-4 py-3 border-r border-base-border w-1/2 max-w-0">
                    <div className="w-full overflow-hidden">
                      <InputSelectDropdown
                        options={productOptions}
                        onOptionChange={(value) =>
                          onProductChange(productIndex, value || null)
                        }
                        defaultSelected={getSelectedOption(prod)}
                        placeholder="Select Product"
                        buttonClassName="h-10"
                        buttonDataTestId={`buyOneGetManyProduct${productIndex + 1}`}
                        listDataTestId="dashBuyOneGetManyProductList"
                      />
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-3 w-1/2">
                    <div className="flex items-center gap-2">
                      <Input
                        name={`amount-${sequenceIndex}-${productIndex}`}
                        value={
                          localQuantities[productIndex] ??
                          (prod?.quantity || '')
                        }
                        onChange={(e) =>
                          onQuantityChange(
                            e as React.ChangeEvent<HTMLInputElement>,
                            productIndex
                          )
                        }
                        type="number"
                        placeholder="0"
                      />
                      <div
                        className={`flex-shrink-0 flex items-center justify-center w-10 h-10 cursor-pointer text-base-foreground transition-colors ${products.length <= 1 ? 'opacity-30 pointer-events-none' : ''}`}
                      >
                        <FontAwesomeIcon
                          icon="fa-regular fa-trash-can"
                          className="text-base"
                          onClick={() =>
                            products.length > 1 && onRemoveProduct(productIndex)
                          }
                        />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={onAddProduct}
        disabled={!canAddMore}
        className="w-full bg-base-muted border-none hover:bg-violet-100 shadow-none h-10 mt-1 mb-1"
      >
        <FontAwesomeIcon
          icon="fa-regular fa-circle-plus"
          className="mr-2 text-base"
        />
        Add New Product
      </Button>
    </div>
  );
};

export default CardProductsTable;
